import { concat, noop } from 'lodash';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Box } from '@mui/material';

import ToastRenderer, { Toast } from './toastr-renderer';
import { useMuiSwitchableTheme } from '../../theme';

interface IToastrProviderProps {
  children: ReactNode;
}

interface IToastrContext {
  push: (t: Toast) => void;
  /**
   * Alias for `push` (preferred name in some call sites/tests).
   */
  pushToast: (t: Toast) => void;
  remove: (id: string | number) => void;
}

const ToastrContext = createContext<IToastrContext>({
  push: noop,
  pushToast: noop,
  remove: noop,
});

const useToastr = () => useContext(ToastrContext);

function ToastrProvider({ children }: IToastrProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const { marginTop } = useMuiSwitchableTheme();

  const updateToasts = useCallback((toast: Toast) => {
    const toastWithId = {
      ...toast,
      id: toast?.id || Date.now() + Math.random(),
    };
    setToasts((arr) =>
      arr.find((t) => t.id === toast.id)
        ? arr
        : concat(
            // Remove existing toasts marked for removal on next push
            arr.filter((t) => !t.removeOnNextPush),
            toastWithId,
          ),
    );
  }, []);

  const removeToast = useCallback((id: string | number) => {
    setToasts((arr) => arr.filter((toast) => toast.id !== id));
  }, []);

  const contextValue = useMemo(
    () => ({
      push: updateToasts,
      pushToast: updateToasts,
      remove: removeToast,
    }),
    [updateToasts, removeToast],
  );

  return (
    <ToastrContext.Provider value={contextValue}>
      <Box
        sx={{
          position: 'fixed',
          top: marginTop + 16,
          right: 16,
          zIndex: 9999,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxWidth: '400px',
          '& > *': {
            pointerEvents: 'auto',
          },
        }}
      >
        {toasts.map((toast, index) => (
          <Box
            key={toast.id || Date.now()}
            sx={{
              // Stagger animation delays for multiple toasts
              animationDelay: `${index * 100}ms`,
            }}
          >
            <ToastRenderer toast={toast} onRemove={removeToast} />
          </Box>
        ))}
      </Box>
      {children}
    </ToastrContext.Provider>
  );
}

export { ToastrContext, ToastrProvider, useToastr };
export type { IToastrContext, IToastrProviderProps };
